import { useState, type ComponentType } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import type { SvgIconProps } from '@mui/material/SvgIcon'
import AccountBoxRoundedIcon from '@mui/icons-material/AccountBoxRounded'
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded'
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded'
import FaceRoundedIcon from '@mui/icons-material/FaceRounded'
import { ProfileCanvas } from './ProfileCanvas'
import { AchievementsPage } from './AchievementsPage'
import { CharacterPage } from './CharacterPage'
import { ProfileDetailsPanel } from '../Profile/ProfileDetailsPanel'
import { useNls } from '../../nls/nlsProvider'
import { getYourPageTabLabel } from '../ExperienceNls'
import { RailChipTabs } from '../Common'

type TabKey = 'PROFILE' | 'IDENTITY' | 'ACHIEVEMENTS' | 'CHARACTER'

type HighlightTarget = {
  kind: 'THEME' | 'BACKGROUND' | 'SOUNDSCAPE'
  cosmeticId: string
}

type YourPageProps = {
  onBack: () => void
  initialTab?: TabKey
  highlightTarget?: HighlightTarget | null
  onHighlightConsumed?: () => void
  onOpenUserSettings?: (section: 'NOTIFICATIONS') => void
  hideFunPack?: boolean
}

const TABS: { key: TabKey; Icon: ComponentType<SvgIconProps> }[] = [
  { key: 'IDENTITY', Icon: BadgeRoundedIcon },
  { key: 'PROFILE', Icon: AccountBoxRoundedIcon },
  { key: 'ACHIEVEMENTS', Icon: EmojiEventsRoundedIcon },
  { key: 'CHARACTER', Icon: FaceRoundedIcon },
]

export const YourPage = ({ onBack: _onBack, initialTab, highlightTarget, onHighlightConsumed, onOpenUserSettings, hideFunPack }: YourPageProps) => {
  const { t } = useNls()
  const [tab, setTab] = useState<TabKey>(initialTab ?? 'IDENTITY')
  const visibleTabs = hideFunPack
    ? TABS.filter(({ key }) => key === 'IDENTITY' || key === 'PROFILE')
    : TABS
  const shownTab: TabKey = hideFunPack && (tab === 'ACHIEVEMENTS' || tab === 'CHARACTER') ? 'IDENTITY' : tab

  return (
    <section className="gPage" aria-label={t('NLSExperienceYourPageAria')}>
      <header className="gHeader gYourPageHeader">
        <div className="gYourPageHeaderLeft">
          <span className="gEyebrow">{t('NLSExperienceYourPageEyebrow')}</span>
          <RailChipTabs
            orientation="horizontal"
            ariaLabel={t('NLSExperienceYourPageSectionsAria')}
            value={shownTab}
            onChange={(k) => setTab(k as TabKey)}
            items={visibleTabs.map(({ key, Icon }) => ({
              key,
              label: getYourPageTabLabel(key, t),
              icon: <Icon fontSize="inherit" />,
            }))}
          />
        </div>
      </header>

      <div className="gTabViewport">
        <AnimatePresence mode="wait">
          <motion.div
            key={shownTab}
            className="gTabPanel"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            {shownTab === 'PROFILE' && <ProfileCanvas />}
            {shownTab === 'IDENTITY' && (
              <ProfileDetailsPanel onOpenNotificationsSettings={() => onOpenUserSettings?.('NOTIFICATIONS')} />
            )}
            {!hideFunPack && shownTab === 'ACHIEVEMENTS' && <AchievementsPage />}
            {!hideFunPack && shownTab === 'CHARACTER' && (
              <CharacterPage highlightTarget={highlightTarget} onHighlightConsumed={onHighlightConsumed} />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  )
}
